// Analytics tab › Guided Build (spec §10): the 5-step wizard —
// Connect → Pick tables → Describe → Generate → Review — over the backend
// analytics orchestrator (/designers/analytics-build + status polling).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import {
  ArrowLeft, Check, Circle, Database, Minus, Search, Sparkles, X,
} from 'lucide-react'
import {
  introspectBiConnection,
  listBiConnections,
  type BiConnection,
} from '@/api/connections'
import {
  analyticsBuild,
  analyticsBuildStatus,
  getTables,
  type AnalyticsBuildArtifact,
  type AnalyticsBuildJob,
} from '@/api/designers'
import type { AnalyticsSection } from './shared'
import { InlineError, TypeChip, TYPE_COLORS, errMessage } from './shared'

const STEPS = ['Connect', 'Pick tables', 'Describe', 'Generate', 'Review']

interface AreaDraft {
  name: string
  description: string
}

interface PickTable {
  name: string
  rows?: number | null
  group: 'fact' | 'dim' | 'stg' | 'other'
}

const GROUP_LABELS: Record<PickTable['group'], string> = {
  fact: 'Measurements (facts) — the numbers',
  dim: 'Lookups (dimensions) — the descriptions',
  stg: 'Staging — usually skip these',
  other: 'Other tables',
}

const GROUP_ORDER: PickTable['group'][] = ['fact', 'dim', 'other', 'stg']

function classify(name: string): PickTable['group'] {
  const u = name.toUpperCase()
  if (/^(FACT|F)_/.test(u)) return 'fact'
  if (/^(DIM|D)_/.test(u)) return 'dim'
  if (/^(STG|STAGE|STAGING|TMP|TEMP|RAW)_/.test(u) || /_(STG|RAW|TMP)$/.test(u)) return 'stg'
  return 'other'
}

/** Section an artifact opens in on the Review step. */
function sectionForKind(kind: string): AnalyticsSection {
  switch ((kind || '').toUpperCase()) {
    case 'M': return 'schema'
    case 'A': return 'areas'
    case 'V': return 'visuals'
    case 'D': return 'dashboards'
    default: return 'overview'
  }
}

export default function GuidedBuildSection({
  project, onGotoSection, onOpenObject, onFinished,
}: {
  project: string
  onGotoSection: (section: AnalyticsSection) => void
  onOpenObject: (section: AnalyticsSection, objName: string) => void
  onFinished: () => void
}) {
  const [step, setStep] = useState(0)

  // ── Step 0 — Connect ──
  const [connections, setConnections] = useState<BiConnection[] | null>(null)
  const [connError, setConnError] = useState<string | null>(null)
  const [picked, setPicked] = useState<string>('') // '' = project's own database

  const loadConnections = useCallback(async () => {
    setConnError(null)
    try {
      setConnections(await listBiConnections(project))
    } catch (err) {
      setConnections([])
      setConnError(errMessage(err, 'Could not load connections'))
    }
  }, [project])

  useEffect(() => { void loadConnections() }, [loadConnections])

  // ── Step 1 — Pick tables ──
  const [tables, setTables] = useState<PickTable[] | null>(null)
  const [tablesError, setTablesError] = useState<string | null>(null)
  const [tablesLoading, setTablesLoading] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [tableFilter, setTableFilter] = useState('')
  const tablesForRef = useRef<string | null>(null)

  const loadTables = useCallback(async (connId: string) => {
    setTablesLoading(true)
    setTablesError(null)
    try {
      let names: { name: string; rows?: number | null }[] = []
      if (connId) {
        const res = await introspectBiConnection(project, connId)
        if (!res.success) throw new Error(res.notes?.join('; ') || 'Introspection failed')
        names = (res.tables || []).map((t) =>
          typeof t === 'string'
            ? { name: t }
            : {
                name: String((t as Record<string, unknown>).name
                  ?? (t as Record<string, unknown>).table
                  ?? (t as Record<string, unknown>).TABLE_NAME ?? ''),
                rows: ((t as Record<string, unknown>).rows
                  ?? (t as Record<string, unknown>).row_count) as number | undefined,
              },
        ).filter((t) => t.name)
      } else {
        // Project's own database: table names from the Database_Design.
        const res = await getTables(project)
        names = ((res?.tables || []) as { name: string }[]).map((t) => ({ name: t.name }))
      }
      const picks: PickTable[] = names.map((t) => ({ ...t, group: classify(t.name) }))
      setTables(picks)
      // Default: everything except staging.
      setSelected(new Set(picks.filter((p) => p.group !== 'stg').map((p) => p.name)))
      tablesForRef.current = connId
    } catch (err) {
      setTables(null)
      setTablesError(errMessage(err, 'Could not read tables from the source'))
    } finally {
      setTablesLoading(false)
    }
  }, [project])

  // ── Step 2 — Describe ──
  const [areas, setAreas] = useState<AreaDraft[]>([{ name: '', description: '' }])
  const [want, setWant] = useState('')

  // ── Step 3 — Generate ──
  const [job, setJob] = useState<AnalyticsBuildJob | null>(null)
  const [starting, setStarting] = useState(false)
  const [startError, setStartError] = useState<string | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [])

  useEffect(() => () => stopPolling(), [stopPolling])

  const startBuild = useCallback(async () => {
    setStarting(true)
    setStartError(null)
    setJob(null)
    stopPolling()
    try {
      const payload = {
        connection_id: picked || undefined,
        business_areas: areas
          .filter((a) => a.name.trim())
          .map((a) => ({ name: a.name.trim(), description: a.description.trim() || undefined })),
        tables: Array.from(selected),
        want: want.trim() || undefined,
        build_home_page: true,
      }
      const res = await analyticsBuild(project, payload)
      setStep(3)
      pollRef.current = setInterval(async () => {
        try {
          const st = await analyticsBuildStatus(project, res.job_id)
          setJob(st.job)
          if (st.job.status !== 'running') stopPolling()
        } catch {
          // transient poll failure — keep trying; a dead job surfaces as
          // repeated failures which the user can escape via Back/Retry.
        }
      }, 2500)
    } catch (err) {
      setStartError(errMessage(err, 'Could not start the analytics build'))
      toast.error(errMessage(err, 'Could not start the analytics build'))
    } finally {
      setStarting(false)
    }
  }, [areas, picked, project, selected, stopPolling, want])

  // ── Derived ──
  const pickedConn = connections?.find((c) => c.CONNECTION_ID === picked)
  const sourceName = picked
    ? (pickedConn?.CONNECTION_NAME || picked)
    : "this project's database"

  const grouped = useMemo(() => {
    const list = (tables || []).filter((t) =>
      !tableFilter.trim() || t.name.toLowerCase().includes(tableFilter.trim().toLowerCase()))
    return GROUP_ORDER
      .map((g) => ({ group: g, rows: list.filter((t) => t.group === g) }))
      .filter((x) => x.rows.length > 0)
  }, [tables, tableFilter])

  const allDone = job?.status === 'done'
  const failed = job?.status === 'failed'

  const artifactsByKind = useMemo(() => {
    const map = new Map<string, AnalyticsBuildArtifact[]>()
    for (const a of job?.artifacts || []) {
      const k = (a.kind || '-').toUpperCase()
      if (!map.has(k)) map.set(k, [])
      map.get(k)!.push(a)
    }
    return map
  }, [job])

  const whyByKind: Record<string, string> = useMemo(() => {
    const out: Record<string, string> = {}
    const stepFor: Record<string, string> = {
      M: 'schemas', A: 'business_areas', V: 'dashboards', D: 'dashboards',
      S: 'home_page', '-': 'navigation',
    }
    for (const [kind, key] of Object.entries(stepFor)) {
      const s = job?.steps.find((x) => x.key === key)
      if (s?.detail) out[kind] = s.detail
    }
    return out
  }, [job])

  // ── Footer wiring ──
  const canNext =
    step === 0 ? connections !== null
      : step === 1 ? selected.size > 0 || (tables !== null && tables.length === 0)
        : step === 2 ? true
          : step === 3 ? allDone
            : true

  const primaryLabel =
    step === 0 ? 'Next: pick tables'
      : step === 1 ? 'Next: describe'
        : step === 2 ? '✦ Generate'
          : step === 3 ? 'Review results'
            : 'Finish'

  const handlePrimary = () => {
    if (step === 0) {
      // (Re)load tables when the source changed since the last read.
      if (tablesForRef.current !== picked || tables === null) void loadTables(picked)
      // Seed one business area named after the project on first pass.
      setAreas((prev) =>
        prev.length === 1 && !prev[0].name && !prev[0].description
          ? [{ name: project.replace(/[_-]+/g, ' '), description: '' }]
          : prev)
      setStep(1)
    } else if (step === 1) {
      setStep(2)
    } else if (step === 2) {
      void startBuild()
    } else if (step === 3) {
      setStep(4)
    } else {
      toast.success('Analytics app published to the project')
      onFinished()
    }
  }

  // ── Renderers ──
  // Circle states (spec §10.1): done = accent + white ✓; active = accent-weak
  // bg + accent text + 2px accent ring; idle = surface-2 + border, faint number.
  const stepCircle = (i: number) => {
    const done = i < step || (i === 3 && step === 3 && allDone)
    const active = i === step
    return (
      <div
        style={{
          width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 14, fontWeight: 700,
          background: done ? 'var(--color-accent)' : active ? 'var(--color-accent-weak)' : 'var(--color-surface-2)',
          border: active ? '2px solid var(--color-accent)' : '1px solid var(--color-border)',
          color: done ? '#fff' : active ? 'var(--color-accent)' : 'var(--color-text-faint)',
        }}
      >
        {done ? <Check size={15} /> : i + 1}
      </div>
    )
  }

  const statusIcon = (status: string) => {
    if (status === 'done') {
      return (
        <span
          style={{
            width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
            background: 'var(--color-success)', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Check size={14} />
        </span>
      )
    }
    if (status === 'running') {
      return <span className="spinner" style={{ width: 22, height: 22, flexShrink: 0 }} />
    }
    if (status === 'failed') {
      return (
        <span
          style={{
            width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
            background: 'var(--color-error)', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <X size={14} />
        </span>
      )
    }
    if (status === 'skipped') {
      return (
        <span
          style={{
            width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
            background: 'var(--color-surface-2)', border: '1px solid var(--color-border)',
            color: 'var(--color-text-secondary)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Minus size={13} />
        </span>
      )
    }
    return (
      <span
        style={{
          width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
          background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)',
          color: 'var(--color-text-secondary)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Circle size={9} />
      </span>
    )
  }

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 10px', fontSize: 15,
    border: '1px solid var(--color-border-strong)', borderRadius: 7,
    background: 'var(--color-input-bg)', color: 'var(--color-text)',
  }

  return (
    <div style={{ maxWidth: 1000, width: '100%', margin: '0 auto', padding: '30px 30px 70px' }}>
      {/* Stepper */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 22 }}>
        {STEPS.map((label, i) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10, flex: i < STEPS.length - 1 ? 1 : undefined }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {stepCircle(i)}
              <span
                style={{
                  fontSize: 15.5,
                  fontWeight: i === step ? 700 : 500,
                  color: i === step
                    ? 'var(--color-text)'
                    : i < step ? 'var(--color-text-secondary)' : 'var(--color-text-faint)',
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div
                style={{
                  flex: 1, height: 2,
                  background: i < step ? 'var(--color-accent)' : 'var(--color-border)',
                }}
              />
            )}
          </div>
        ))}
      </div>

      {/* Step body card (spec §10.1): surface, radius 16 */}
      <div
        style={{
          background: 'var(--color-bg-secondary)',
          border: '1px solid var(--color-border)',
          borderRadius: 16,
          padding: '30px 32px',
          minHeight: 420,
        }}
      >
        {/* ── Step 0: Connect ── */}
        {step === 0 && (
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Connect your data</h2>
            <p style={{ margin: '6px 0 18px', fontSize: 15.5, lineHeight: 1.55, color: 'var(--color-text-secondary)' }}>
              Choose where the numbers live. You can use this project's own database, or link an
              external one.
            </p>
            {connError && <InlineError message={connError} onRetry={() => void loadConnections()} />}
            <div style={{ display: 'grid', gap: 8, maxWidth: 620, marginTop: connError ? 12 : 0 }}>
              {/* Project's own database */}
              <SourceCard
                code="APP"
                name="This project's database"
                sub="Built in — no connection needed"
                pickedNow={picked === ''}
                onPick={() => setPicked('')}
              />
              {connections === null ? (
                <div
                  style={{
                    height: 56, borderRadius: 10, opacity: 0.55,
                    background: 'var(--color-surface-2)', border: '1px solid var(--color-border)',
                  }}
                />
              ) : (
                connections.map((c) => (
                  <SourceCard
                    key={c.CONNECTION_ID}
                    code={(c.DATABASE_TYPE || 'DB').slice(0, 2).toUpperCase()}
                    name={c.CONNECTION_NAME || c.CONNECTION_ID}
                    sub={c.DATABASE_TYPE || 'database'}
                    pickedNow={picked === c.CONNECTION_ID}
                    onPick={() => setPicked(c.CONNECTION_ID)}
                  />
                ))
              )}
              <button
                onClick={() => onGotoSection('connections')}
                style={{
                  padding: '11px 14px', borderRadius: 10, cursor: 'pointer',
                  border: '1.5px dashed var(--color-border)', background: 'transparent',
                  color: 'var(--color-accent)', fontSize: 14, fontWeight: 600, textAlign: 'left',
                }}
              >
                + Create a new connection — manage connections…
              </button>
            </div>
          </div>
        )}

        {/* ── Step 1: Pick tables ── */}
        {step === 1 && (
          <div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 300 }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Pick the tables to analyze</h2>
                <p style={{ margin: '6px 0 0', fontSize: 15.5, lineHeight: 1.55, color: 'var(--color-text-secondary)' }}>
                  Read live from <b>{sourceName}</b>. We grouped them and suggested which are
                  measurements (facts) vs. lookups (dimensions).
                </p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 27, fontWeight: 700 }}>{selected.size}</div>
                <div style={{ fontSize: 14.5, color: 'var(--color-text-secondary)' }}>tables selected</div>
              </div>
            </div>

            <div style={{ marginTop: 16 }}>
              {tablesLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 20, color: 'var(--color-text-secondary)', fontSize: 15.5 }}>
                  <span className="spinner" style={{ width: 18, height: 18 }} />
                  Reading tables from {sourceName}…
                </div>
              ) : tablesError ? (
                <InlineError message={tablesError} onRetry={() => void loadTables(picked)} />
              ) : !tables || tables.length === 0 ? (
                <p style={{ fontSize: 15.5, lineHeight: 1.55, color: 'var(--color-text-secondary)' }}>
                  No tables found in {sourceName}. You can continue — the build will describe the
                  model from your words alone, or go back and pick another source.
                </p>
              ) : (
                <>
                  <div style={{ position: 'relative', maxWidth: 320, marginBottom: 10 }}>
                    <Search
                      size={13}
                      style={{
                        position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)',
                        color: 'var(--color-text-secondary)',
                      }}
                    />
                    <input
                      style={{ ...inputStyle, paddingLeft: 28 }}
                      placeholder="Search tables…"
                      value={tableFilter}
                      onChange={(e) => setTableFilter(e.target.value)}
                    />
                  </div>
                  <div
                    style={{
                      border: '1px solid var(--color-border)', borderRadius: 12,
                      overflow: 'hidden', maxHeight: 380, overflowY: 'auto',
                    }}
                  >
                    {grouped.map(({ group, rows }) => (
                      <div key={group}>
                        <div
                          style={{
                            padding: '8px 14px', fontSize: 13.5, fontWeight: 600,
                            color: 'var(--color-text-secondary)',
                            background: 'var(--color-surface-2)',
                            borderBottom: '1px solid var(--color-border)',
                            display: 'flex', alignItems: 'center', gap: 10,
                          }}
                        >
                          <span style={{ flex: 1 }}>{GROUP_LABELS[group]}</span>
                          <button
                            className="btn btn-ghost btn-sm"
                            style={{ fontSize: 13 }}
                            onClick={() => {
                              const all = rows.every((r) => selected.has(r.name))
                              setSelected((prev) => {
                                const next = new Set(prev)
                                rows.forEach((r) => { if (all) next.delete(r.name); else next.add(r.name) })
                                return next
                              })
                            }}
                          >
                            {rows.every((r) => selected.has(r.name)) ? 'Unselect all' : 'Select all'}
                          </button>
                        </div>
                        {rows.map((t) => (
                          <label
                            key={t.name}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 10,
                              padding: '8px 14px', cursor: 'pointer', fontSize: 15.5,
                              borderBottom: '1px solid var(--color-border)',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selected.has(t.name)}
                              onChange={() =>
                                setSelected((prev) => {
                                  const next = new Set(prev)
                                  if (next.has(t.name)) next.delete(t.name)
                                  else next.add(t.name)
                                  return next
                                })}
                            />
                            <span style={{ fontFamily: 'monospace', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {t.name}
                            </span>
                            <span
                              style={{
                                fontSize: 13, fontWeight: 600, padding: '2px 8px', borderRadius: 6,
                                color: group === 'fact'
                                  ? 'var(--color-info)'
                                  : group === 'dim' ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                                background: group === 'fact'
                                  ? 'var(--color-info-weak)'
                                  : group === 'dim' ? 'var(--color-accent-weak)' : 'var(--color-surface-2)',
                              }}
                            >
                              {group === 'fact' ? 'measurement' : group === 'dim' ? 'lookup' : group === 'stg' ? 'staging' : 'table'}
                            </span>
                            {typeof t.rows === 'number' && (
                              <span style={{ fontSize: 14.5, color: 'var(--color-text-secondary)', width: 70, textAlign: 'right' }}>
                                {t.rows.toLocaleString()}
                              </span>
                            )}
                          </label>
                        ))}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ── Step 2: Describe ── */}
        {step === 2 && (
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Describe what you want</h2>
            <p style={{ margin: '6px 0 18px', fontSize: 15.5, lineHeight: 1.55, color: 'var(--color-text-secondary)' }}>
              Group the tables into business areas in plain language. These become the topics your
              team (and the AI Assistant) can explore.
            </p>
            <div style={{ display: 'grid', gap: 10, maxWidth: 680 }}>
              {areas.map((a, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex', gap: 8, alignItems: 'flex-start',
                    background: 'var(--color-surface-2)', border: '1px solid var(--color-border)',
                    borderRadius: 11, padding: '11px 13px',
                  }}
                >
                  <TypeChip code="A" size={22} />
                  <div style={{ flex: 1, display: 'grid', gap: 6, minWidth: 0 }}>
                    <input
                      style={{ ...inputStyle, fontWeight: 600 }}
                      placeholder="Area name — e.g. Sales"
                      value={a.name}
                      onChange={(e) =>
                        setAreas((prev) => prev.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                    />
                    <input
                      style={inputStyle}
                      placeholder="What it covers — e.g. Orders, revenue and customer performance"
                      value={a.description}
                      onChange={(e) =>
                        setAreas((prev) => prev.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))}
                    />
                  </div>
                  {areas.length > 1 && (
                    <button
                      className="btn btn-ghost btn-sm"
                      title="Remove this area"
                      onClick={() => setAreas((prev) => prev.filter((_, j) => j !== i))}
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
              <div>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setAreas((prev) => [...prev, { name: '', description: '' }])}
                >
                  + Add business area
                </button>
              </div>
              <div style={{ marginTop: 8 }}>
                <label style={{ display: 'block', fontSize: 14.5, fontWeight: 600, marginBottom: 4 }}>
                  What do you want to see?
                  <span style={{ fontWeight: 400, color: 'var(--color-text-secondary)' }}>
                    {' '}(optional — helps us pick the starter charts)
                  </span>
                </label>
                <textarea
                  style={{ ...inputStyle, resize: 'vertical' }}
                  rows={3}
                  placeholder="e.g. Monthly sales by region and product, stock cover in days, and supplier on-time delivery %."
                  value={want}
                  onChange={(e) => setWant(e.target.value)}
                />
              </div>
            </div>
          </div>
        )}

        {/* ── Step 3: Generate ── */}
        {step === 3 && (
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>
              {failed ? 'The build hit a problem' : allDone ? 'All done' : 'Building your analytics app…'}
            </h2>
            <p style={{ margin: '6px 0 18px', fontSize: 15.5, lineHeight: 1.55, color: 'var(--color-text-secondary)' }}>
              {failed
                ? 'One of the steps failed — the details are below. You can retry with the same inputs.'
                : 'One pass creates everything and wires it into your menu. Watch each piece land below.'}
            </p>
            {startError && (
              <div style={{ marginBottom: 12 }}>
                <InlineError message={startError} onRetry={() => void startBuild()} />
              </div>
            )}
            <div style={{ display: 'grid', gap: 6, maxWidth: 720 }}>
              {(job?.steps || []).map((s) => (
                <div
                  key={s.key}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 12,
                    padding: '10px 13px', borderRadius: 11,
                    background: s.status === 'pending' ? 'transparent' : 'var(--color-surface-2)',
                  }}
                >
                  {statusIcon(s.status)}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 14.5, fontWeight: 600,
                        color: s.status === 'pending' ? 'var(--color-text-faint)' : 'var(--color-text)',
                      }}
                    >
                      {s.label}
                    </div>
                    {(s.status === 'done' || s.status === 'failed' || s.status === 'skipped') && s.detail && (
                      <div
                        style={{
                          fontSize: 14.5, marginTop: 2,
                          color: s.status === 'failed' ? 'var(--color-error)' : 'var(--color-text-secondary)',
                        }}
                      >
                        {s.detail}
                      </div>
                    )}
                    {s.artifacts.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                        {s.artifacts.map((a, i) => (
                          <span
                            key={`${a.kind}:${a.name}:${i}`}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 6,
                              padding: '3px 9px', borderRadius: 7, fontSize: 14,
                              border: '1px solid var(--color-border)',
                              background: 'var(--color-bg)',
                            }}
                          >
                            <TypeChip code={(a.kind || '-').toUpperCase()} size={14} />
                            {a.title || a.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  {s.status === 'done' && (
                    <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--color-success)', flexShrink: 0 }}>
                      Created
                    </span>
                  )}
                </div>
              ))}
              {!job && !startError && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 16, fontSize: 15.5, lineHeight: 1.55, color: 'var(--color-text-secondary)' }}>
                  <span className="spinner" style={{ width: 18, height: 18 }} />
                  Starting the build…
                </div>
              )}
            </div>
            {failed && (
              <div style={{ marginTop: 16, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                {job?.error && (
                  <span style={{ fontSize: 14.5, color: 'var(--color-error)' }}>{job.error}</span>
                )}
                <button className="btn btn-primary btn-sm" onClick={() => void startBuild()}>
                  Retry with the same inputs
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => setStep(2)}>
                  Change the inputs
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Step 4: Review ── */}
        {step === 4 && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span
                style={{
                  width: 34, height: 34, borderRadius: '50%',
                  background: 'var(--color-success)', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <Check size={18} />
              </span>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Your analytics app is ready</h2>
            </div>
            <p style={{ margin: '8px 0 18px', fontSize: 15.5, lineHeight: 1.55, color: 'var(--color-text-secondary)' }}>
              Here's what we created and why. Open any item to fine-tune it.
            </p>
            <div style={{ display: 'grid', gap: 8, maxWidth: 760 }}>
              {Array.from(artifactsByKind.entries()).map(([kind, arts]) =>
                arts.map((a, i) => (
                  <button
                    key={`${kind}:${a.name}:${i}`}
                    onClick={() => {
                      const section = sectionForKind(kind)
                      if (section === 'overview') onGotoSection('overview')
                      else onOpenObject(section, a.name)
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left',
                      padding: '12px 15px', borderRadius: 11, cursor: 'pointer',
                      background: 'var(--color-surface-2)', border: '1px solid var(--color-border)',
                      color: 'var(--color-text)',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--color-accent)' }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--color-border)' }}
                  >
                    <TypeChip code={kind} size={26} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontWeight: 600, fontSize: 15 }}>
                        {a.title || a.name}
                      </span>
                      {whyByKind[kind] && (
                        <span style={{ display: 'block', fontSize: 14.5, color: 'var(--color-text-secondary)' }}>
                          {whyByKind[kind]}
                        </span>
                      )}
                    </span>
                    <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--color-accent)', flexShrink: 0 }}>
                      Open →
                    </span>
                  </button>
                )))}
              {(job?.artifacts.length || 0) === 0 && (
                <p style={{ fontSize: 15.5, lineHeight: 1.55, color: 'var(--color-text-secondary)' }}>
                  Nothing was created — check the Generate step for details.
                </p>
              )}
            </div>
            <div style={{ marginTop: 22 }}>
              <div style={{ fontSize: 14.5, fontWeight: 600, marginBottom: 8 }}>Suggested next steps</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button className="btn btn-secondary btn-sm" onClick={() => onGotoSection('schema')}>
                  + Tune row security
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => onGotoSection('dashboards')}>
                  + Arrange the dashboard
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => onGotoSection('areas')}>
                  + Teach the Assistant more
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', alignItems: 'center', marginTop: 16, gap: 12 }}>
        <button
          className="btn btn-secondary"
          style={{ visibility: step === 0 || step === 3 ? 'hidden' : 'visible' }}
          onClick={() => setStep((s) => Math.max(0, s - 1))}
        >
          <ArrowLeft size={15} />
          Back
        </button>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 14.5, color: 'var(--color-text-secondary)' }}>
          Step {step + 1} of {STEPS.length}
        </span>
        <button
          className="btn btn-primary"
          disabled={!canNext || starting || (step === 3 && !allDone)}
          onClick={handlePrimary}
        >
          {step === 2 && starting ? 'Starting…' : primaryLabel}
        </button>
      </div>
    </div>
  )
}

function SourceCard({
  code, name, sub, pickedNow, onPick,
}: {
  code: string
  name: string
  sub: string
  pickedNow: boolean
  onPick: () => void
}) {
  return (
    <button
      onClick={onPick}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left',
        padding: '11px 14px', borderRadius: 10, cursor: 'pointer',
        border: pickedNow ? '1.5px solid var(--color-accent)' : '1px solid var(--color-border)',
        background: pickedNow ? 'var(--color-accent-weak)' : 'var(--color-surface-2)',
        color: 'var(--color-text)',
      }}
    >
      <span
        style={{
          width: 34, height: 34, borderRadius: 9, flexShrink: 0,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          background: 'var(--color-raised)', border: '1px solid var(--color-border)',
          fontFamily: 'monospace', fontSize: 13, fontWeight: 700,
          color: 'var(--color-text-secondary)',
        }}
      >
        {code === 'APP' ? <Database size={15} style={{ color: TYPE_COLORS['-'] }} /> : code}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontWeight: 600, fontSize: 14.5 }}>{name}</span>
        <span style={{ display: 'block', fontSize: 14, color: 'var(--color-text-secondary)' }}>{sub}</span>
      </span>
      <span
        style={{
          width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          background: pickedNow ? 'var(--color-accent)' : 'transparent',
          border: pickedNow ? 'none' : '1px solid var(--color-border)',
          color: '#fff',
        }}
      >
        {pickedNow && <Check size={12} />}
      </span>
    </button>
  )
}
